// src/components/modals/FileImportModal.tsx
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "../ui/dialog";
import { getDocumentIcon, getFileType } from "@/utils/helpers";
import {
  DocumentTextIcon,
  FolderArrowDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { useUploadProjectDocument } from "@/api/ProjetsApi";
import { ProjectDocument } from "@/types/project";
import { failedToast } from "@/utils/toasts";
import SmallLoader from "../SmallLoader";

const FileImportModal: React.FC = () => {
  const [localFiles, setLocalFiles] = useState<ProjectDocument[]>([]); // Local file state for new files
  const [open, setOpen] = useState(false);
  const { uploadProjectDocument } = useUploadProjectDocument();
  const [crudFiles, setCrudFiles] = useState<FileList | null>(null);
  const [loading, setLoading] = useState(false);

  const handleRemoveFile = (fileId: string) => {
    const uploadedFiles: ProjectDocument[] = localFiles.filter(
      (file) => file.id !== fileId
    );

    setLocalFiles(uploadedFiles);
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    setCrudFiles(files); // Store the file list in state

    if (files) {
      const uploadedFiles: ProjectDocument[] = Array.from(files).map(
        (file, index) => ({
          id: index.toString(),
          title: file.name,
          path: file.webkitRelativePath || "", // File path (if available)
          subTitle: "Uploaded file", // Placeholder subtitle
          logoUrl: getDocumentIcon(getFileType(file.name)), // Function to fetch the logo URL (if any)
          downloadDate: new Date().toISOString(), // Current date as ISO string
          lastModifiedDate: file.lastModified
            ? new Date(file.lastModified).toISOString()
            : new Date().toISOString(),
          fileSize: (file.size / 1024).toFixed(2) + " KB", // Human-readable file size
          status: "uploading", // Initial status
          document_type: getFileType(file.name), // Determine file type based on the extension
        })
      );

      setLocalFiles((prev) => [...prev, ...uploadedFiles]); // Add files to the local state
    }
  };

  const handleContinue = async () => {
    if (crudFiles) {
      if (crudFiles.length === 0) return;

      setLoading(true);
      const { success, failedDocs } = await uploadProjectDocument(crudFiles);
      setLoading(false);

      if (success) {
        setLocalFiles([]); // Reset local state
        setOpen(false); // Close modal
      } else {
        console.error("Failed members:", failedDocs);
        failedToast(`Some members failed to add: ${failedDocs.join(", ")}`);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="bg-rajapiNavyBlue rounded-lg text-white py-2 px-6 hover:shadow-md">
          Upload files
        </button>
      </DialogTrigger>
      <DialogContent className="w-full max-w-xl p-8">
        <DialogHeader>
          <div className="w-full flex">
            <div className="bg-white p-4 rounded-2xl w-fit">
              <DocumentTextIcon className="w-6 h-6 text-rajapiBlue" />
            </div>
            {loading && <SmallLoader modal={true} />}
          </div>
        </DialogHeader>
        <h2 className="text-xl font-medium text-black text-blue-950">
          Importer des documents necessaires
        </h2>
        <h2 className="text-xs font-medium text-gray">
          Commencez à créer et gérer les documents de votre projet en quelques
          clics seulement !
        </h2>
        <div className="mt-4">
          <label
            htmlFor="file-upload"
            className="flex flex-col w-full p-4 border-2  rounded-2xl text-center cursor-pointer text-gray-500 hover:border-blue-500 focus:outline-none bg-white justify-center items-center space-y-4"
          >
            <FolderArrowDownIcon className="text-rajapiBlue w-14 h-14" />
            <span className="text-black font-bold text-xl">
              Faites glisser et déposez ici
            </span>
            <span className="text-grey-700 font-normal text-sm text-nowrap">
              Le type de fichier doit être(PowerPoint, PDF, Word Doc, etc).
            </span>
            <input
              id="file-upload"
              type="file"
              multiple
              className="hidden"
              onChange={handleFileUpload}
            />
          </label>
        </div>
        {localFiles.length > 0 && (
          <ul className=" space-y-2 bg-white p-4 rounded-2xl">
            {localFiles.map((file) => (
              <li
                key={file.id}
                className="flex items-center justify-between bg-[#ECEBE9] px-4 py-2 rounded-xl"
              >
                <div className="flex items-center">
                  {file.logoUrl && (
                    <img
                      src={file.logoUrl}
                      alt={file.document_type}
                      className="w-8 h-8 mr-2"
                    />
                  )}
                  <div>
                    <p className="text-md ">{file.title}</p>
                    <p className="text-xs text-gray-500 flex space-x-2">
                      <span>{file.subTitle} </span>
                      <span
                        className={`text-xs ${
                          file.status === "completed"
                            ? "text-green-500"
                            : "text-gray-500"
                        }`}
                      >
                        {file.status}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveFile(file.id)}
                  className="flex items-center space-x-2"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex justify-end">
          <button
            className="mr-2 bg-rajapiBlue py-4 px-24 rounded-xl text-white text-sm"
            type="submit"
            onClick={() => handleContinue()}
            disabled={localFiles.length === 0}
          >
            Continue
          </button>
          <button
            className="mr-2 bg-white py-4 px-24 rounded-xl text-blue-950 text-sm"
            onClick={() => {
              setOpen(false);
              setLocalFiles([]);
            }}
          >
            Annuler
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FileImportModal;
